import * as React from "react";
import { cn } from "lib/utils";
import { generateTemplate, TemplateGenerationError, type TemplateGenerationErrorKind } from "core";
import {
  X, Loader2, Send, Bot, CheckCircle2, AlertTriangle, Wand2, Sparkles, ExternalLink,
} from "lucide-react";

export type QueryModalProps = {
  addAiTemplate: (data: any) => void;
  templateIcon?: boolean;
  onOpenChange?: (open: boolean) => void;
  templateId?: string | number;
  currentHtml?: string;
};
export type QueryModalHandle = {
  openModal: () => void;
};
type Msg = {
  id: string;
  role: "user" | "assistant";
  content: string;
  status?: "generating" | "success" | "error";
  /** For errors with a next step: entitlement → upgrade, quota → billing. */
  errorKind?: TemplateGenerationErrorKind;
};

const SUGGESTIONS = [
  "Welcome series",
  "Abandoned cart",
  "Flash sale offer",
  "Product launch",
  "Re-engagement",
  "Monthly newsletter",
];

const AiTemplate = React.forwardRef<QueryModalHandle, QueryModalProps>(
  ({ addAiTemplate, templateIcon, onOpenChange, templateId, currentHtml }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [msgs, setMsgs] = React.useState<Msg[]>([]);
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLTextAreaElement>(null);
    const [sid, setSid] = React.useState<string | null>(null);
    /** A generation the server may still be running. Resending the same input
        reuses its request_id, so it is picked up rather than charged again. */
    const pendingRef = React.useRef<{ key: string; requestId: string } | null>(null);
    const [focused, setFocused] = React.useState(false);

    React.useEffect(() => {
      onOpenChange?.(open);
    }, [open, onOpenChange]);
    React.useEffect(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [msgs]);
    React.useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 200); }, [open]);

    const autoResize = React.useCallback(() => {
      const el = inputRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 80) + "px";
      el.style.overflowY = el.scrollHeight > 80 ? "auto" : "hidden";
    }, []);

    React.useEffect(() => { autoResize(); }, [query, autoResize]);
    React.useImperativeHandle(ref, () => ({
      openModal: () => setOpen(true),
    }));

    const sendMsg = async (overrideText?: string) => {
      const text = (overrideText ?? query).trim();
      if (text.length <= 1 || loading) return;
      const uMsg: Msg = {
        id: `${Date.now()}`, role: "user",
        content: text
      };
      const aMsg: Msg = {
        id: `${Date.now() + 1}`, role: "assistant",
        content: "",
        status: "generating",
      };
      setMsgs(p => [...p, uMsg, aMsg]);
      setQuery("");
      setLoading(true);
      const input = sid
        ? { prompt: text, session_id: sid }
        : { prompt: text, template_id: templateId, html: templateId ? currentHtml : undefined };
      const key = JSON.stringify(input);
      try {
        const pending = pendingRef.current?.key === key ? pendingRef.current : null;
        pendingRef.current = null;
        const d = await generateTemplate(input, { requestId: pending?.requestId });
        let html: any = d?.html ?? d;
        if (typeof html === "string")
          html = html.replace(/^```(?:html)?\s*/i, "").replace(/\s*```\s*$/, "").trim();
        if (d?.session_id) setSid(d.session_id);
        setMsgs(p => p.map(m => m.id === aMsg.id ? { ...m, content: html, status: "success" } : m));
        setTimeout(() => addAiTemplate(html), 300);
      } catch (e: any) {
        if (e instanceof TemplateGenerationError && e.kind === "in_progress" && e.requestId)
          pendingRef.current = { key, requestId: e.requestId };
        const err = e?.message || "Generation failed";
        const errorKind = e instanceof TemplateGenerationError ? e.kind : undefined;
        setMsgs(p => p.map(m => m.id === aMsg.id ? { ...m, content: err, status: "error", errorKind } : m));
      } finally {
        setLoading(false);
      }
    };

    const close = () => {
      if (!loading) { setOpen(false); setQuery(""); setMsgs([]); setSid(null); pendingRef.current = null; }
    };

    const onKey = (e: React.KeyboardEvent) => {
      if (e.key === "Escape" && !loading) close();
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); sendMsg(undefined); }
    };


    return (
      <>
        {/* Trigger (non-toolbar mode) */}
        {!templateIcon && (
          <button type="button" onClick={() => setOpen(true)}
            className="ms-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12px] font-semibold
              text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20
              border border-blue-200 dark:border-blue-700/40 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all">
            <Wand2 className="w-3.5 h-3.5" /> AI Generate
          </button>
        )}

        {/* ── Mobile backdrop ── */}
        {open && (
          <div
            className="fixed inset-0 bg-black/30 z-40 sm:hidden"
            onClick={close}
            aria-hidden="true"
          />
        )}

        {/* ═════════════════════ PANEL ═════════════════════ */}
        <div
          className={cn(
            "z-50 flex flex-col bg-surface dark:bg-[#0d1117]",
            // ── Mobile: fixed full-width bottom sheet ──
            "fixed inset-x-0 bottom-0 h-[80dvh] rounded-t-2xl",
            "border-t border-line/80 dark:border-slate-800",
            // ── Tablet/Desktop: absolute right panel ──
            "sm:absolute sm:inset-x-auto sm:bottom-auto",
            "sm:top-0 sm:right-0 sm:h-full sm:w-[320px] sm:rounded-none",
            "sm:border-t-0 sm:border-l",
            // Transition: slide-up on mobile, slide-right on desktop
            "transition-transform duration-300 ease-out will-change-transform",
            open
              ? "translate-y-0 sm:translate-x-0"
              : "translate-y-full sm:translate-y-0 sm:translate-x-full"
          )}
          style={{ boxShadow: "0 -8px 32px rgba(0,0,0,0.08), -4px 0 24px rgba(0,0,0,0.04)" }}
          onKeyDown={onKey} role="dialog" aria-modal="true" aria-labelledby="ai-panel-title"
        >
          {/* Mobile drag handle */}
          <div className="sm:hidden flex-shrink-0 flex justify-center pt-2.5 pb-0.5" aria-hidden="true">
            <div className="w-8 h-[3px] rounded-full bg-gray-300 dark:bg-slate-600" />
          </div>

          {/* ── TOP ACCENT BAR ── */}
          <div className="h-[3px] flex-shrink-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

          {/* ── HEADER ── */}
          <div className="flex-shrink-0 px-4 py-3.5 border-b border-line-subtle dark:border-slate-800
            flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Gradient icon badge */}
              <div className="relative flex-shrink-0">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg,#2563eb,#7c3aed)", boxShadow: "0 4px 12px rgba(99,102,241,0.30)" }}>
                  <Bot className="w-4 h-4 text-white" />
                </div>
                {/* Pulse dot */}
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400
                  ring-2 ring-white dark:ring-[#0d1117]" />
              </div>

              {/* Title block */}
              <div>
                <div className="flex items-center gap-1.5">
                  <h2 id="ai-panel-title" className="text-ui font-bold text-content dark:text-white leading-none">
                    Merko AI
                  </h2>
                </div>
                <p className="text-micro text-content-subtle dark:text-slate-500 leading-tight mt-0.5 font-semibold">
                  Email marketing templates · AI-powered
                </p>
              </div>
            </div>

            {/* Close */}
            <button onClick={close} disabled={loading}
              className="w-7 h-7 rounded-lg flex items-center justify-center
                text-content-subtle dark:text-slate-500
                hover:bg-gray-100 dark:hover:bg-slate-800
                hover:text-content-secondary dark:hover:text-slate-300
                transition-all disabled:opacity-40"
              aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* ── BODY (scrollable) ── */}
          <div className="flex-1 overflow-y-auto min-h-0"
            style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(99,102,241,.15) transparent" }}>

            {msgs.length === 0 ? (
              /* ── Premium empty state ── */
              <div className="flex flex-col items-center pt-8 pb-6 px-4 text-center select-none">

                {/* Hero orb */}
                <div className="relative mb-5 flex-shrink-0">
                  {/* Ambient glow */}
                  <div className="absolute inset-0 rounded-full scale-[2.2] opacity-40 blur-2xl"
                    style={{ background: "radial-gradient(circle,#818cf8 0%,#6366f1 50%,transparent 100%)" }} />
                  {/* Icon tile */}
                  <div className="w-[48px] h-[48px] rounded-2xl flex items-center justify-center relative"
                    style={{
                      background: "linear-gradient(145deg,#6366f1 0%,#4f46e5 50%,#3b82f6 100%)",
                      boxShadow: "0 0 0 6px rgba(99,102,241,.10), 0 8px 24px rgba(99,102,241,.35)",
                    }}>
                    <Wand2 className="w-5 h-5 text-white relative z-10" />
                  </div>
                  {/* Sparkle badge */}
                  <div className="absolute -top-1 -right-1 w-[13px] h-[13px] rounded-[3px]
                    bg-amber-400 flex items-center justify-center shadow-sm shadow-amber-400/50">
                    <Sparkles className="w-1.5 h-1.5 text-white" />
                  </div>
                </div>

                {/* Copy */}
                <h3 className="text-[13.5px] font-bold text-content dark:text-white leading-snug mb-1.5">
                  Generate email templates<br />with AI in seconds
                </h3>
                <p className="text-[10.5px] text-content-subtle dark:text-slate-500 leading-relaxed max-w-[200px] mb-5">
                  Describe your campaign and Merko AI will write a complete email.
                </p>

                {/* Suggestion chips — 2-col grid */}
                <div className="grid grid-cols-2 gap-1.5 w-full">
                  {SUGGESTIONS.map(s => (
                    <button key={s} type="button"
                      onClick={() => sendMsg(s)}
                      className="px-2 py-2 rounded-lg text-2xs font-semibold text-center leading-tight
                        bg-surface dark:bg-slate-800/80
                        border border-line dark:border-slate-700/70
                        text-content-muted dark:text-slate-400
                        hover:border-indigo-300 dark:hover:border-indigo-600/60
                        hover:text-indigo-600 dark:hover:text-indigo-400
                        hover:bg-indigo-50/80 dark:hover:bg-indigo-900/20
                        shadow-sm transition-all duration-150 active:scale-[0.97]">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* ── Chat Messages ── */
              <div className="px-3 py-3 space-y-4">
                {msgs.map((msg) => (
                  <div key={msg.id} className={cn(
                    "flex gap-2.5",
                    msg.role === "user" ? "flex-row-reverse" : "flex-row"
                  )}>
                    {/* Avatar */}
                    <div className={cn(
                      "w-6 h-6 rounded-lg flex-shrink-0 mt-0.5 flex items-center justify-center",
                      msg.role === "user"
                        ? "text-white text-[7px] font-black"
                        : "bg-gray-50 dark:bg-slate-800 border border-line dark:border-slate-700"
                    )}
                      style={msg.role === "user"
                        ? { background: "linear-gradient(135deg,#2563eb,#7c3aed)", boxShadow: "0 2px 8px rgba(99,102,241,.30)" }
                        : {}}>
                      {msg.role === "user"
                        ? <span>YOU</span>
                        : <Bot className="w-3 h-3 text-content-subtle dark:text-slate-400" />
                      }
                    </div>

                    {/* Bubble */}
                    <div className={cn(
                      "max-w-[82%] px-3.5 py-2.5 text-[11.5px] leading-relaxed",
                      msg.role === "user"
                        ? "text-white rounded-2xl rounded-tr-sm shadow-sm"
                        : msg.status === "error"
                          ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-800/30 rounded-2xl rounded-tl-sm"
                          : "bg-gray-50 dark:bg-slate-800/80 text-gray-700 dark:text-slate-200 border border-line-subtle dark:border-slate-700/60 rounded-2xl rounded-tl-sm shadow-sm"
                    )}
                      style={msg.role === "user"
                        ? { background: "linear-gradient(135deg,#2563eb,#7c3aed)" }
                        : {}}>

                      {msg.status === "generating" ? (
                        <span className="flex gap-1 items-center h-4">
                          {[0, 1, 2].map(i => (
                            <span key={i}
                              className="w-1.5 h-1.5 rounded-full bg-indigo-300 dark:bg-indigo-500 animate-bounce"
                              style={{ animationDelay: `${i * 0.15}s` }} />
                          ))}
                        </span>
                      ) : msg.role === "assistant" && msg.status === "success" ? (
                        <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-semibold">
                          <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                          Template applied to editor!
                        </span>
                      ) : msg.status === "error" ? (
                        <span className="flex flex-col gap-2">
                          <span className="flex items-start gap-1.5">
                            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5 text-red-400" />
                            {msg.content}
                          </span>
                          {(msg.errorKind === "entitlement" || msg.errorKind === "quota") && (
                            /* New tab, so unsaved work in the editor is not lost. */
                            <a
                              href={msg.errorKind === "entitlement" ? "/payments?tab=plan" : "/payments"}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="self-start inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold
                                text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                            >
                              {msg.errorKind === "entitlement" ? "View plans" : "Manage billing"}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </span>
                      ) : (
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
            )}
          </div>

          {/* ── INPUT ── */}
          <div className="flex-shrink-0 px-3 pb-3 pt-2.5
            border-t border-line-subtle dark:border-slate-800">
            <div className={cn(
              "flex items-end gap-2 rounded-xl px-3 py-2.5 transition-all duration-200",
              "bg-surface dark:bg-slate-800/80",
              focused
                ? "ring-2 ring-indigo-400/30 shadow-lg shadow-indigo-500/10 border border-indigo-300 dark:border-indigo-600/50"
                : "border border-line dark:border-slate-700/60 shadow-sm"
            )}>
              {/* Textarea */}
              <textarea
                ref={inputRef}
                value={query}
                onChange={e => { setQuery(e.target.value); autoResize(); }}
                onKeyDown={onKey}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Describe the email you want…"
                disabled={loading}
                rows={1}
                className="flex-1 text-meta resize-none bg-transparent border-none outline-none
                  text-gray-800 dark:text-slate-100
                  placeholder:text-content-subtle dark:placeholder:text-slate-500
                  disabled:opacity-50 leading-relaxed py-0.5"
                style={{ scrollbarWidth: "none", overflowY: "hidden", minHeight: 20 }}
              />

              {/* Send button — icon only, circular */}
              <button
                onClick={() => sendMsg()}
                disabled={loading || query.trim().length <= 1}
                className={cn(
                  "w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center",
                  "transition-all duration-150 active:scale-[0.92]",
                  query.trim().length > 1 && !loading
                    ? "text-white shadow-sm shadow-indigo-500/30 hover:shadow-indigo-500/50"
                    : "text-gray-300 dark:text-slate-600 bg-gray-100 dark:bg-slate-700/50"
                )}
                style={query.trim().length > 1 && !loading
                  ? { background: "linear-gradient(135deg,#2563eb,#7c3aed)", boxShadow: "0 2px 10px rgba(99,102,241,.35)" }
                  : {}}
                title="Send (Ctrl+Enter)">
                {loading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Send className="w-3 h-3" />
                }
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }
);

AiTemplate.displayName = "AiTemplate";
export default AiTemplate;
